import React, { ReactNode, useMemo, useState } from 'react';
import isFunction, { isReactMemoOrLazy } from '../../react-data-grid/utils/isFunction';
import { Link } from '@fluentui/react-components';
import getIn from '../../connectedForm/utils/getIn';
import ModalContext from '../../Context/ModalContext';
import { ViewContextProvider } from '../../Context/ViewContext';
import { IDependentValues, IFormatterProps } from '../../../@types/workspaceTypes';
import NumericFormatter from './NumericFormatter';
import FluentModal from '../../modals/FluentModal';

export type IModalFormatterOptions = {
  modalContent?: any;
  modalProps?:
    | ((
        value?: any,
        dependentValues?: IDependentValues
      ) => {
        [key: string]: any;
      })
    | {
        [key: string]: any;
      };
  entityId?: string; //e.g. 'supplier.id'
  entityPath?: string;
  pageView?: boolean;
  asButton?: boolean;
  linkClass?: string;
  isModal?: (rowData: any) => boolean;
};

/**
 * Custom formatter to handle numeric formatter display styling with Modal formatter 'click to display detail' capability on top.
 */
export const NumericModalFormatter = (props: IFormatterProps) => {
  return (
    <ModalFormatter {...props}>
      <NumericFormatter {...props} />
    </ModalFormatter>
  );
};

/**
 * This component replaces "DeprecatedModalFormatter" - It wraps the modalContent with a fluent-ui v8 modal for consistency with other modals throughout P4.
 *  - It will display the content in a modal large enough for most forms/workspaces - If alternatives sizes are used, create a custom formatter.
 *  - It is generally recommended that for new code, we create a new, custom formatter anyway, for flexibility - See 'ProductFormModalFormatter' as an example.
 *
 * @param value - Value to display, provided by the cell.
 * @param dependentValues - Cell meta information.
 * @param children - Optional children prop to override the display formatting (Use a custom formatter to add this).
 */
export const ModalFormatter = ({
  value,
  dependentValues,
  children,
}: IFormatterProps<any, IModalFormatterOptions> & {
  children?: ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const modalContext = useMemo(() => ({ onClose: () => setIsOpen(false) }), []);

  const {
    options: { entityPath, entityId, modalProps, modalContent, isModal },
    rowData,
  } = dependentValues;
  const path = entityPath ? `${entityPath}.` : '';
  const formId = getIn(rowData, path + entityId);

  const content = useMemo(() => {
    if (!isOpen) return false;

    const baseModalProps: { [key: string]: any } =
      typeof modalProps === 'function' ? modalProps(value, dependentValues) : modalProps;

    const modalContentProps = {
      key: 'modal',
      pageView: true,
      ...baseModalProps,
      onClose: () => setIsOpen(false),
      entityId: formId,
      entityPath: entityPath,
      match: { params: { id: formId } },
      rowData: rowData ?? null,
      overrideParams: { id: formId },
      modalClose: true,
    };

    //Note - Expectation is to consistently see 'Lazy' here - Helps avoid import loops/etc.
    if (isFunction(modalContent) || isReactMemoOrLazy(modalContent)) {
      const ModalContent = modalContent; //JSX Nodes must start with an uppercase letter in the variable name.
      return <ModalContent {...modalContentProps} />;
    } else if (React.isValidElement(modalContent)) {
      return React.cloneElement(modalContent, modalContentProps);
    }
    throw new Error('ModalFormatter defined without modalContent - Nothing to display');
  }, [dependentValues, entityPath, formId, isOpen, modalContent, modalProps, value, rowData]);

  const viewContext = useMemo(
    () => ({
      uniqueName: 'ModalFormatter',
    }),
    []
  );

  if (dependentValues.rowData?.importedTransaction || !value || (isModal && !isModal(rowData))) {
    return <div style={{ textAlign: 'right' }}>{children ?? value}</div>;
  }

  return (
    <>
      <Link onClick={() => setIsOpen(true)}>{children ?? value}</Link>
      <ModalContext.Provider value={modalContext}>
        <FluentModal isOpen={isOpen}>
          <div style={{ height: '97vh', width: '80vw' }}>
            <ViewContextProvider value={viewContext}>{content}</ViewContextProvider>
          </div>
        </FluentModal>
      </ModalContext.Provider>
    </>
  );
};

export default ModalFormatter;
